import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { message, Select, Button } from "antd";
import ConfirmationModal from "./ConfirmationModal";
import ParticipantDetailsModal from "./ParticipantDetailsModal";
import { CSVLink } from "react-csv";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DashboardStyle = styled.body`
  margin-left: 280px;
  overflow-x: hidden;
`;

const { Option } = Select;

const Participants = () => {
  const { token } = useAuth();
  const [participantsData, setParticipantsData] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [participantIdToRemove, setParticipantIdToRemove] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [timePeriod, setTimePeriod] = useState(30);
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingStatuses, setEditingStatuses] = useState({});
  const [selectedProviders, setSelectedProviders] = useState({}); // Track selected provider for each participant
  const [fundingLoading, setFundingLoading] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});

  // New state for participant details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetails, setParticipantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchParticipantsData = async () => {
    try {
      if (!token) {
        console.error("No token available. Please log in again.");
        setLoading(false);
        return;
      }

      const participantsResponse = await axios.get(
        `${BASE_URL}/companies/members/list?time_period=${timePeriod}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      if (participantsResponse.status === 200) {
        const rawData = participantsResponse.data.data;

        // Group participants by ID to handle multiple provider subscriptions
        const groupedData = rawData.reduce((acc, participant) => {
          const key = participant.id;

          if (!acc[key]) {
            acc[key] = {
              id: participant.id,
              employee_name: `${participant.first_name} ${participant.last_name}`,
              user_phone: participant.user_phone,
              user_email: participant.user_email,
              activity_count: participant.activity_count,
              activity_status: participant.activity_status,
              invited_on: formatUserCreatedAt(participant.invited_on),
              joined_on: formatUserCreatedAt(participant.joined_on),
              // Store the raw joined_on date for sorting
              raw_joined_on: participant.joined_on,
              providers: [],
              // Store the full participant data for details modal
              rawData: participant
            };
          }

          // Add provider subscription to the participant
          if (participant.provider_name) {
            acc[key].providers.push({
              provider_name: participant.provider_name,
              subscription_name: participant.subscription_name,
              provider_id: participant.provider_id || participant.provider_name,
              activity_count: participant.activity_count,
              activity_status: participant.activity_status,
              subscription_start_date: participant.subscription_start_date,
              subscription_expiry_date: participant.subscription_expiry_date,
              subscription_last_renewed_at: participant.subscription_last_renewed_at,
              subscription_renewal_count: participant.subscription_renewal_count
            });
          }

          return acc;
        }, {});

        const formattedData = Object.values(groupedData).map(participant => ({
          ...participant,
          // Set default selected provider (first one)
          selectedProvider: participant.providers.length > 0 ? participant.providers[0] : null,
          // For display, use the first provider's data as default
          provider_name: participant.providers.length > 0 ? participant.providers[0].provider_name : "None"
        })).sort((a, b) => {
          // Sort by joined_on date, most recent first (descending order)
          // Handle null/undefined dates by treating them as very old dates
          const dateA = a.raw_joined_on ? new Date(a.raw_joined_on) : new Date(0);
          const dateB = b.raw_joined_on ? new Date(b.raw_joined_on) : new Date(0);
          
          // Most recent first (descending order)
          return dateB - dateA;
        });

        setParticipantsData(formattedData);
        setFilteredParticipants(formattedData);

        // Initialize selected providers state
        const initialSelectedProviders = {};
        formattedData.forEach(participant => {
          if (participant.providers.length > 0) {
            initialSelectedProviders[participant.id] = participant.providers[0];
          }
        });
        setSelectedProviders(initialSelectedProviders);
      } else {
        throw new Error("Data retrieval not successful");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching Participants data:", error);
      setError(error);
      setLoading(false);
    }
  };

  const fetchParticipantDetails = async (participantId) => {
    setDetailsLoading(true);
    try {
      // Find the participant from our existing data
      const participant = participantsData.find(p => p.id === participantId);
      if (!participant) {
        throw new Error("Participant not found");
      }

      // Extract details from the raw data and providers
      const details = {
        personal_credits: participant.rawData.personal_credits || 0,
        company_credits: participant.rawData.company_credits || 0,
        user_currency: participant.rawData.user_currency || null,
        subscriptions: participant.providers || []
      };

      setParticipantDetails(details);
      setSelectedParticipant(participant);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching participant details:", error);
      message.error("Failed to load participant details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipantsData();
  }, [timePeriod, token]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingStatus !== null) {
        const isEditIcon = event.target.classList.contains('fa-pencil');
        const isDropdown = event.target.closest('.status-dropdown-container');
        const isAntSelect = event.target.closest('.ant-select') || event.target.closest('.ant-select-dropdown');

        if (!isEditIcon && !isDropdown && !isAntSelect) {
          setEditingStatus(null);
          setEditingStatuses({});
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingStatus]);

  useEffect(() => {
    if (searchQuery.trim() !== "") {
      const filtered = participantsData.filter((participant) =>
        participant.employee_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
      
      // Maintain the same sorting after filtering
      const sortedFiltered = filtered.sort((a, b) => {
        const dateA = a.raw_joined_on ? new Date(a.raw_joined_on) : new Date(0);
        const dateB = b.raw_joined_on ? new Date(b.raw_joined_on) : new Date(0);
        return dateB - dateA;
      });
      
      setFilteredParticipants(sortedFiltered);
    } else {
      setFilteredParticipants(participantsData);
    }
  }, [searchQuery, participantsData]);

  const formatUserCreatedAt = (dateString) => {
    if (!dateString) return "—";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const removeParticipant = async (id) => {
    setParticipantIdToRemove(id);
    setShowConfirmationModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/companies/members/${participantIdToRemove}/remove`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        setParticipantsData((prevData) =>
          prevData.filter(
            (participant) => participant.id !== participantIdToRemove
          )
        );
        message.success("Employee deleted successfully");
      } else {
        throw new Error("Failed to remove participant");
      }
    } catch (error) {
      console.error("Error removing participant:", error);
      message.error("Failed to remove participant. Please try again.");
    } finally {
      setShowConfirmationModal(false);
      setParticipantIdToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowConfirmationModal(false);
    setParticipantIdToRemove(null);
  };

  const handleStatusChange = async (participantId) => {
    const newStatus = editingStatuses[participantId];
    if (!newStatus) {
      message.error("Please select a status");
      return;
    }

    setStatusUpdateLoading(prev => ({ ...prev, [participantId]: true }));

    try {
      const response = await axios.put(
        `${BASE_URL}/companies/members/status/${participantId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Check if request was successful (200-299 status code)
      if (response.status >= 200 && response.status < 300) {
        // Check if response has success status OR assume success if no status field
        if (!response.data.status || response.data.status === "success") {
          const displayStatus = getDisplayStatus(newStatus);

          // Update both the main participant data and the provider data
          setParticipantsData(prevData =>
            prevData.map(p => {
              if (p.id === participantId) {
                // Update the main activity_status
                const updatedParticipant = { ...p, activity_status: displayStatus };

                // Update the provider data as well
                if (p.providers && p.providers.length > 0) {
                  updatedParticipant.providers = p.providers.map(provider => ({
                    ...provider,
                    activity_status: displayStatus
                  }));

                  // Update selectedProvider if it exists
                  if (p.selectedProvider) {
                    updatedParticipant.selectedProvider = {
                      ...p.selectedProvider,
                      activity_status: displayStatus
                    };
                  }
                }

                return updatedParticipant;
              }
              return p;
            })
          );

          // Update the selected providers state
          setSelectedProviders(prevSelected => {
            if (prevSelected[participantId]) {
              return {
                ...prevSelected,
                [participantId]: {
                  ...prevSelected[participantId],
                  activity_status: displayStatus
                }
              };
            }
            return prevSelected;
          });

          // Update filtered participants (this will trigger re-render)
          setFilteredParticipants(prevFiltered =>
            prevFiltered.map(p => {
              if (p.id === participantId) {
                const updatedParticipant = { ...p, activity_status: displayStatus };

                if (p.providers && p.providers.length > 0) {
                  updatedParticipant.providers = p.providers.map(provider => ({
                    ...provider,
                    activity_status: displayStatus
                  }));

                  if (p.selectedProvider) {
                    updatedParticipant.selectedProvider = {
                      ...p.selectedProvider,
                      activity_status: displayStatus
                    };
                  }
                }

                return updatedParticipant;
              }
              return p;
            })
          );

          message.success("Status updated successfully");
          setEditingStatus(null);
          setEditingStatuses(prev => {
            const updated = { ...prev };
            delete updated[participantId];
            return updated;
          });
        } else {
          throw new Error(response.data.message || "Failed to update status");
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      
      // Handle different error types
      if (error.response) {
        // API returned an error response
        const errorMessage = error.response.data?.detail || 
                            error.response.data?.message || 
                            "Failed to update status. Please try again.";
        message.error(errorMessage);
      } else {
        // Network or other error
        message.error("Failed to update status. Please try again.");
      }
    } finally {
      setStatusUpdateLoading(prev => {
        const updated = { ...prev };
        delete updated[participantId];
        return updated;
      });
    }
  };

  const handleNotifyActiveUsers = async () => {
    setFundingLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/companies/fund-notified-users`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      if (response.data.status === "success") {
        message.success(response.data.message);
      } else {
        throw new Error("Failed to notify users");
      }
    } catch (error) {
      console.error("Error notifying users:", error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error("Failed to notify users. Please try again.");
      }
    } finally {
      setFundingLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "green";
      case "Inactive":
        return "red";
      case "On Hold":
        return "orange";
      default:
        return "black";
    }
  };

  const getApiStatus = (displayStatus) => {
    switch (displayStatus) {
      case "Active":
        return "active";
      case "Inactive":
        return "inactive";
      case "On Hold":
        return "onhold";
      default:
        return displayStatus.toLowerCase();
    }
  };

  const getDisplayStatus = (apiStatus) => {
    switch (apiStatus.toLowerCase()) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      case "onhold":
        return "On Hold";
      default:
        return apiStatus;
    }
  };

  const handleEditStatusClick = (participantId, currentStatus, event) => {
    event.stopPropagation();
    event.preventDefault();

    if (editingStatus === participantId) {
      return;
    }

    setEditingStatus(participantId);
    setEditingStatuses({
      [participantId]: getApiStatus(currentStatus)
    });
  };

  const handleCancelEdit = (participantId, event) => {
    event.stopPropagation();
    setEditingStatus(null);
    setEditingStatuses(prev => {
      const updated = { ...prev };
      delete updated[participantId];
      return updated;
    });
  };

  const handleStatusSelectChange = (participantId, value) => {
    setEditingStatuses(prev => ({
      ...prev,
      [participantId]: value
    }));
  };

  const handleProviderChange = (participantId, providerData) => {
    setSelectedProviders(prev => ({
      ...prev,
      [participantId]: providerData
    }));

    // Update the participant data to reflect the selected provider's info
    setParticipantsData(prevData =>
      prevData.map(p =>
        p.id === participantId
          ? {
            ...p,
            provider_name: providerData.provider_name,
            activity_count: providerData.activity_count,
            activity_status: providerData.activity_status
          }
          : p
      )
    );
  };

  const getCurrentProviderData = (participant) => {
    return selectedProviders[participant.id] || participant.selectedProvider || (participant.providers.length > 0 ? participant.providers[0] : null);
  };

  const handleShowDetails = (participant) => {
    fetchParticipantDetails(participant.id);
  };

  const csvData = filteredParticipants.map((participant) => {
    const currentProvider = getCurrentProviderData(participant);
    return {
      Participant: participant.employee_name,
      "Activities participated": currentProvider?.activity_count || participant.activity_count,
      Invited: participant.invited_on,
      Joined: participant.joined_on,
      Provider: currentProvider?.provider_name || "None",
      Status: currentProvider?.activity_status || participant.activity_status,
    };
  });

  const getUniqueProviders = (participant) => {
    if (!participant.providers || participant.providers.length === 0) {
      return [];
    }

    // Remove duplicates based on provider_name
    const uniqueProviders = participant.providers.filter((provider, index, self) =>
      index === self.findIndex((p) => p.provider_name === provider.provider_name)
    );

    return uniqueProviders;
  };

  const hasMultipleUniqueProviders = (participant) => {
    const uniqueProviders = getUniqueProviders(participant);
    return uniqueProviders.length > 1;
  };

  return (
    <DashboardStyle>
      <div className="content-container">
        <br /> <br />
        <div className="row">
          <div className="col d-flex align-items-center">
            <h4 className="me-3">Summary</h4>
            <div className="d-flex align-items-center">
              <select onChange={(e) => setTimePeriod(parseInt(e.target.value))}>
                <option value="30">30</option>
                <option value="60">60</option>
                <option value="90">90</option>
              </select>
              <span
                className="ms-2"
                style={{ fontSize: "17px", color: "#3c2f61" }}
              >
                days
              </span>
            </div>
          </div>

          <div className="col">
            <div className="search-bar">
              <input
                type="text"
                className="form-control"
                placeholder="Search name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
        <br />

        {loading ? (
          <main className="main-content position-relative border-radius-lg card">
            <div className="container-fluid py-4" style={{ fontWeight: "bold" }}>
              <div className="text-center spinner">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </main>
        ) : (
          <div>
            <main className="main-content border-radius-lg card container-fluid">
              <div className="table-responsive">
                <div>
                  <div className="card-body">
                    {filteredParticipants.length === 0 ? (
                      <p>No results found</p>
                    ) : (
                      <table className="table">
                        <thead>
                          <tr style={{ color: "black" }}>
                            <th scope="col">Participant</th>
                            <th scope="col">Joined</th>
                            <th scope="col">Provider</th>
                            <th scope="col" className="text-center">Account Status</th>
                            <th scope="col" className="text-center">Change Status</th>
                            <th scope="col" className="text-center">Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredParticipants.map((participant) => {
                            const currentProvider = getCurrentProviderData(participant);
                            return (
                              <tr key={participant.id}>
                                <td>
                                  <i
                                    className="fa fa-user"
                                    style={{ color: "#3c2f61", fontSize: "17px" }}
                                  ></i>{" "}
                                  {participant.employee_name}
                                </td>
                                <td>
                                  {participant.joined_on}
                                </td>
                                <td >
                                  {hasMultipleUniqueProviders(participant) ? (
                                    <Select
                                      value={currentProvider?.provider_name || "None"}
                                      onChange={(value) => {
                                        const uniqueProviders = getUniqueProviders(participant);
                                        const selectedProvider = uniqueProviders.find(p => p.provider_name === value);
                                        if (selectedProvider) {
                                          handleProviderChange(participant.id, selectedProvider);
                                        }
                                      }}
                                      style={{ width: 120 }}
                                      size="small"
                                    >
                                      {getUniqueProviders(participant).map((provider, index) => (
                                        <Option key={index} value={provider.provider_name}>
                                          {provider.provider_name}
                                        </Option>
                                      ))}
                                    </Select>
                                  ) : (
                                    currentProvider?.provider_name || "None"
                                  )}
                                </td>
                                <td className="text-center">
                                  {editingStatus === participant.id ? (
                                    <div
                                      className="status-dropdown-container"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        position: "relative",
                                        minHeight: '40px',
                                        minWidth: '200px',
                                        zIndex: 1000,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}
                                    >
                                      <Select
                                        value={editingStatuses[participant.id] || ''}
                                        onChange={(value) => handleStatusSelectChange(participant.id, value)}
                                        style={{ width: 120 }}
                                        placeholder="Select status"
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Option value="active">Active</Option>
                                        <Option value="inactive">Inactive</Option>
                                        <Option value="onhold">On Hold</Option>
                                      </Select>
                                      <Button
                                        type="primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusChange(participant.id);
                                        }}
                                        size="small"
                                        loading={statusUpdateLoading[participant.id] || false}
                                        disabled={!editingStatuses[participant.id]}
                                        style={{
                                          backgroundColor: "#3c2f61",
                                          borderColor: "#3c2f61"
                                        }}
                                      >
                                        Save
                                      </Button>
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        color: getStatusColor(currentProvider?.activity_status || participant.activity_status)
                                      }}
                                    >
                                      {currentProvider?.activity_status || participant.activity_status}
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  {editingStatus === participant.id ? (
                                    <span style={{ color: 'grey', fontSize: '14px' }}>Editing...</span>
                                  ) : (
                                    <i
                                      className="fa fa-pencil"
                                      style={{
                                        color: "grey",
                                        cursor: "pointer",
                                        fontSize: "18px",
                                        marginTop: 5,
                                      }}
                                      onMouseOver={(e) =>
                                        (e.target.style.color = "blue")
                                      }
                                      onMouseOut={(e) =>
                                        (e.target.style.color = "grey")
                                      }
                                      onClick={(e) =>
                                        handleEditStatusClick(participant.id, currentProvider?.activity_status || participant.activity_status, e)
                                      }
                                    ></i>
                                  )}
                                </td>
                                <td className="text-center">
                                  <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                                    <i
                                      className="fa fa-info-circle"
                                      style={{
                                        color: "grey",
                                        cursor: "pointer",
                                        fontSize: "18px",
                                      }}
                                      onMouseOver={(e) =>
                                        (e.target.style.color = "#3c2f61")
                                      }
                                      onMouseOut={(e) =>
                                        (e.target.style.color = "grey")
                                      }
                                      onClick={() => handleShowDetails(participant)}
                                      title="View detailed information"
                                    ></i>
                                    <i
                                      className="fa fa-trash"
                                      style={{
                                        color: "grey",
                                        cursor: "pointer",
                                        fontSize: "18px",
                                      }}
                                      onMouseOver={(e) =>
                                        (e.target.style.color = "red")
                                      }
                                      onMouseOut={(e) =>
                                        (e.target.style.color = "grey")
                                      }
                                      onClick={() =>
                                        removeParticipant(participant.id)
                                      }
                                      title="Remove participant"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="card-footer">
                    <div className="row">
                      <div className="col-6">
                        <Link
                          className="btn btn-primary btn-sm"
                          to="/onboarding"
                          style={{
                            backgroundColor: "#3c2f61",
                            color: "white",
                          }}
                        >
                          Invite participant(s)
                        </Link>
                      </div>
                      <div className="col-6 text-end">
                        <CSVLink
                          data={csvData}
                          filename={"participants.csv"}
                          className="btn btn-primary btn-sm"
                          style={{
                            border: "2px solid #3c2f61",
                            color: "#3c2f61",
                            backgroundColor: "white",
                          }}
                        >
                          Download CSV file
                        </CSVLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        )}

        {showConfirmationModal && (
          <ConfirmationModal
            message="Are you sure you want to delete this participant?"
            onConfirm={handleConfirmRemove}
            onCancel={handleCancelRemove}
          />
        )}

        {/* Participant Details Modal */}
        <ParticipantDetailsModal
          visible={showDetailsModal}
          onCancel={() => {
            setShowDetailsModal(false);
            setSelectedParticipant(null);
            setParticipantDetails(null);
          }}
          participant={selectedParticipant}
          participantDetails={participantDetails}
        />
      </div>
    </DashboardStyle>
  );
};

export default Participants;